import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { MCP_CLIENTS } from "../client-config";
import type { McpClientId } from "../types";

type MCPInstallButtonProps = {
  clientId: McpClientId;
  gatewayName: string;
  gatewaySlug: string;
  enginePublicUrl: string;
  onDeepLinkClick?: (clientId: McpClientId, gatewaySlug: string) => void;
  onDeepLinkError?: (clientId: McpClientId, error: unknown) => void;
};

export function MCPInstallButton({
  clientId,
  gatewayName,
  gatewaySlug,
  enginePublicUrl,
  onDeepLinkClick,
  onDeepLinkError,
}: MCPInstallButtonProps) {
  const client = MCP_CLIENTS[clientId];

  const { deepLink, buildError } = useMemo(() => {
    if (!client.supportsDeepLink) {
      return { deepLink: null, buildError: null };
    }
    try {
      return {
        deepLink: client.buildDeepLink({
          enginePublicUrl,
          gatewayName,
          slug: gatewaySlug,
        }),
        buildError: null,
      };
    } catch (err) {
      return { deepLink: null, buildError: err };
    }
  }, [client, enginePublicUrl, gatewayName, gatewaySlug]);

  useEffect(() => {
    if (buildError != null) {
      onDeepLinkError?.(clientId, buildError);
    }
  }, [buildError, onDeepLinkError, clientId]);

  const handleClick = useCallback(() => {
    onDeepLinkClick?.(clientId, gatewaySlug);
  }, [onDeepLinkClick, clientId, gatewaySlug]);

  if (!deepLink) {
    return null;
  }

  return (
    <a
      aria-label={`Install ${client.name} MCP gateway`}
      className="group flex w-full cursor-pointer items-center gap-4 rounded-lg border border-foreground/20 bg-card p-4 transition-all hover:border-foreground/40 hover:bg-muted/50"
      href={deepLink}
      onClick={handleClick}
      rel="noopener"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center [&>svg]:h-5 [&>svg]:w-5">
        {client.logo}
      </span>
      <div className="min-w-0 flex-1">
        <span className="block font-medium text-sm">Add to {client.name}</span>
      </div>
      <ArrowRight
        aria-hidden
        className="h-5 w-5 shrink-0 text-foreground transition-transform group-hover:translate-x-0.5"
      />
    </a>
  );
}
